import { useEffect, useState } from 'react';
import { format } from 'timeago.js';

/**
 * Component that renders a timestamp formatted using timeago. Updates every
 * 60s.
 *
 * props:
 * - ts: an ISO 8601 timestamp
 *
 * The class derived its displayed string in two places that always agreed:
 * `getDerivedStateFromProps` re-formatted `props.ts` on every render (so a `ts`
 * prop change showed the new relative time in the same frame, no stale flicker)
 * and a 60s `setInterval` re-formatted the same `props.ts` to advance the
 * relative time while the prop stayed constant.
 *
 * Here the displayed value is simply `format(ts)` computed during render — that
 * is exactly the gDSFP behavior (always fresh on a prop change, zero stale
 * frame). The 60s interval no longer formats anything itself; it just bumps a
 * `tick` counter to force a re-render, which re-evaluates `format(ts)` with the
 * current wall-clock time. Same 60s cadence, same mount-tick semantics (the
 * first tick fires at 60s, matching `setInterval`), same output string.
 */
// timeago.js v4 dropped the factory: format() is a stateless function, so
// calling it directly is equivalent to the old timeago().format().
export interface TimeAgoTickerProps {
  /** An ISO 8601 timestamp. */
  ts: string;
}

export default function TimeAgoTicker({ ts }: TimeAgoTickerProps) {
  // `tick` is intentionally unused — it exists only so `setTick` can force a
  // re-render on the 60s interval (see the component doc above).
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setTick((t) => t + 1);
    }, 60000);
    return () => {
      clearInterval(timer);
    };
  }, []);

  return format(ts);
}
